/**
 * Every billing string, Czech first, then English (the app is cs + en only). Dates and money
 * use Czech formatting everywhere (design system: Copy).
 */
import { czPlural, formatCzk } from "@/lib/pricing/plans";

export type BillingLocale = "cs" | "en";

export function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("cs-CZ");
}

function daysLeftCs(n: number): string {
  if (n === 1) return "zbývá 1 den";
  if (n >= 2 && n <= 4) return `zbývají ${n} dny`;
  return `zbývá ${n} dní`;
}

export const billingCopy = {
  cs: {
    tab: "Předplatné",
    planTitle: "Tarif",
    status: {
      free: "Bezplatný přístup",
      trial: "Zkušební verze",
      active: "Aktivní",
      pastDue: "Platba se nezdařila",
      ending: (date: string) => `Končí ${date}`,
      none: "Bez tarifu",
    },
    planName: (name: string) => `Zettl ${name}`,
    trialLine: (n: number, date: string) =>
      `Celá sada Firma zdarma, ${daysLeftCs(n)} (do ${date}).`,
    trialEnded: (date: string) => `Zkušební verze skončila ${date}.`,
    nextPayment: (date: string, amount: number) => `Další platba ${date}: ${formatCzk(amount)}.`,
    firstPayment: (date: string, amount: number) =>
      `První platba ${date}: ${formatCzk(amount)}. Do té doby běží zkušební verze.`,
    ending: (date: string) => `Předplatné skončí ${date}. Do té doby vše funguje jako dnes.`,
    pastDue: "Stripe zkusí platbu znovu. Zkontrolujte prosím kartu ve správě plateb.",
    freeLine: (plan: string, until: string | null) =>
      `Bezplatný přístup · tarif ${plan}${until ? ` do ${until}` : ""}.`,
    nonePrompt: "Vyberte si tarif níže.",
    notOwner: "Předplatné platí vlastník firmy.",
    changePlan: "Změnit tarif",
    managePayments: "Spravovat platby",
    chooseTitle: "Vyberte tarif",
    chooseDesc: "Měsíční platba kartou přes Stripe. Tarif můžete kdykoli změnit nebo zrušit.",
    perMonth: "měsíčně",
    current: "Váš tarif",
    selected: "Vybráno",
    choose: "Vybrat",
    pay: "Pokračovat k platbě",
    payLaterNote: (date: string) => `Zaplatíte až po skončení zkušební verze, ${date}.`,
    vatNote: "Nejsme plátci DPH, uvedené ceny jsou konečné.",
    usageTitle: "Využití",
    usageDesc: (plan: string) => `Proti limitům tarifu ${plan}, za firmy, které tarif pokrývá.`,
    usageCompanies: "firmy",
    usageFio: "účty u Fio",
    usageUsers: "uživatelé",
    aiLine: (n: string) => `AI přečte až ${n} dokladů měsíčně.`,
    checkoutSuccess: "Děkujeme. Platba proběhla, tarif se tu objeví do minuty.",
    checkoutCancel: "Platba nebyla dokončena. Nic jsme neúčtovali.",
    errors: {
      not_owner: "Tarif může změnit jen vlastník firmy.",
      no_customer: "Zatím tu nejsou žádné platby.",
      unavailable: "Platby teď nejdou spustit. Zkuste to prosím později.",
      rate_limited: "Příliš mnoho pokusů. Zkuste to prosím za pár minut.",
      test_mode_org_only: "Testovací platby jdou jen u testovací firmy.",
      failed: "Něco se nepovedlo. Zkuste to prosím znovu.",
    },
    loadError: "Předplatné se nepodařilo načíst.",
    bannerTrial: (n: number) => `Zkušební verze: ${daysLeftCs(n)}.`,
    bannerTrialEnded: "Zkušební verze skončila.",
    bannerPastDue: "Platba za předplatné se nezdařila.",
    bannerPastDueMember: "Dejte prosím vědět vlastníkovi firmy.",
    bannerChoose: "Vybrat tarif",
    bannerFixCard: "Zkontrolovat kartu",
    dismiss: "Zavřít",
  },
  en: {
    tab: "Subscription",
    planTitle: "Plan",
    status: {
      free: "Free access",
      trial: "Free trial",
      active: "Active",
      pastDue: "Payment failed",
      ending: (date: string) => `Ends ${date}`,
      none: "No plan",
    },
    planName: (name: string) => `Zettl ${name}`,
    trialLine: (n: number, date: string) =>
      `The whole Firma suite for free, ${n} ${n === 1 ? "day" : "days"} left (until ${date}).`,
    trialEnded: (date: string) => `The free trial ended on ${date}.`,
    nextPayment: (date: string, amount: number) => `Next payment ${date}: ${formatCzk(amount)}.`,
    firstPayment: (date: string, amount: number) =>
      `First payment ${date}: ${formatCzk(amount)}. The free trial runs until then.`,
    ending: (date: string) =>
      `The subscription ends on ${date}. Everything works as today until then.`,
    pastDue: "Stripe will retry the payment. Please check your card under Manage payments.",
    freeLine: (plan: string, until: string | null) =>
      `Free access · ${plan} plan${until ? ` until ${until}` : ""}.`,
    nonePrompt: "Choose a plan below.",
    notOwner: "The company's owner pays for the subscription.",
    changePlan: "Change plan",
    managePayments: "Manage payments",
    chooseTitle: "Choose a plan",
    chooseDesc: "Monthly card payment through Stripe. Change or cancel your plan at any time.",
    perMonth: "a month",
    current: "Your plan",
    selected: "Selected",
    choose: "Choose",
    pay: "Continue to payment",
    payLaterNote: (date: string) => `You pay when the free trial ends, on ${date}.`,
    vatNote: "We are not VAT registered; the prices are final.",
    usageTitle: "Usage",
    usageDesc: (plan: string) =>
      `Against the ${plan} plan's limits, across the companies it covers.`,
    usageCompanies: "companies",
    usageFio: "Fio accounts",
    usageUsers: "users",
    aiLine: (n: string) => `AI reads up to ${n} documents a month.`,
    checkoutSuccess:
      "Thank you. The payment went through; your plan shows up here within a minute.",
    checkoutCancel: "The payment wasn't completed. Nothing was charged.",
    errors: {
      not_owner: "Only the company's owner can change the plan.",
      no_customer: "There are no payments yet.",
      unavailable: "Payments can't be started right now. Please try again later.",
      rate_limited: "Too many attempts. Please try again in a few minutes.",
      test_mode_org_only: "Test payments work only for the test company.",
      failed: "Something went wrong. Please try again.",
    },
    loadError: "The subscription couldn't be loaded.",
    bannerTrial: (n: number) => `Free trial: ${n} ${n === 1 ? "day" : "days"} left.`,
    bannerTrialEnded: "Your free trial has ended.",
    bannerPastDue: "The subscription payment failed.",
    bannerPastDueMember: "Please let the company's owner know.",
    bannerChoose: "Choose a plan",
    bannerFixCard: "Check your card",
    dismiss: "Dismiss",
  },
};

export type BillingCopy = (typeof billingCopy)["cs"];

export function billingCopyFor(language: string): BillingCopy {
  return (billingCopy[language as BillingLocale] ?? billingCopy.cs) as BillingCopy;
}
